"use client";

import { useState, useEffect } from "react";
import { Loader2, CheckCircle } from "lucide-react";
import {
  useSenOTPMutation,
  useVerifyOTPMutation,
} from "@/redux/features/optVerification/optVerification";

export interface OtEnquiryFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  preferredDate: string;
  reasonForEnquiry: string;
  emailVerified?: boolean;
}

export interface OtEnquiryFormProps {
  onDataChange?: (data: OtEnquiryFormData) => void;
  defaultData?: Partial<OtEnquiryFormData>;
  divisionId?: string;
  requireEmailVerification?: boolean;
}

// Form Label Component
const FormLabel = ({
  label,
  required = false,
  hint,
}: {
  label: string;
  required?: boolean;
  hint?: string;
}) => (
  <div className="flex flex-wrap items-center gap-2.5">
    <div className="flex items-center gap-0.5">
      <span className="font-onest text-sm font-medium text-neutral-800">
        {label}
      </span>
      {required && (
        <span className="font-medium text-base text-red-600">*</span>
      )}
    </div>
    {hint && (
      <span className="font-onest text-sm font-light text-neutral-800/40">
        {hint}
      </span>
    )}
  </div>
);

// Form Input Component
const FormInput = ({
  placeholder,
  value,
  onChange,
  type = "text",
  icon,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  icon?: React.ReactNode;
}) => (
  <div className="flex items-center justify-between overflow-hidden rounded-[10px] border border-[#C01610]/30 px-[17px] py-[11px]">
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-transparent font-onest text-sm font-normal text-neutral-800 placeholder:text-neutral-500/50 focus:outline-none"
    />
    {icon && <div className="ml-2 shrink-0">{icon}</div>}
  </div>
);

// OTP Input Component
const OtpInput = ({
  value,
  onChange,
  onVerify,
  onResend,
  isVerifying,
  error,
  timeRemaining,
  resendCooldown,
  isExpired,
}: {
  value: string;
  onChange: (value: string) => void;
  onVerify: () => void;
  onResend: () => void;
  isVerifying: boolean;
  error?: string | null;
  timeRemaining: number;
  resendCooldown: number;
  isExpired: boolean;
}) => {
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-[10px] border border-[#C01610]/30 p-4">
      <div className="flex items-center justify-between">
        <span className="font-onest text-sm font-medium text-neutral-800">
          Enter the verification code sent to your email
        </span>
        {!isExpired && (
          <span
            className={`font-onest text-sm font-medium ${
              timeRemaining <= 60 ? "text-red-500" : "text-neutral-500"
            }`}
          >
            {formatTime(timeRemaining)}
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="Enter 6-digit code"
          maxLength={6}
          disabled={isExpired}
          className="flex-1 rounded-[10px] border border-[#C01610]/30 px-4 py-2 font-onest text-base font-medium tracking-widest focus:border-[#C01610] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <button
          type="button"
          onClick={onVerify}
          disabled={value.length !== 6 || isVerifying || isExpired}
          className="rounded-[10px] bg-[#C01610] px-4 py-2 font-onest text-sm font-medium text-white transition-colors hover:bg-[#C01610]/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isVerifying ? <Loader2 className="size-5 animate-spin" /> : "Verify"}
        </button>
      </div>
      {error && <span className="font-onest text-sm text-red-500">{error}</span>}
      {isExpired && (
        <span className="font-onest text-sm text-red-500">
          Code expired. Please request a new one.
        </span>
      )}
      <button
        type="button"
        onClick={onResend}
        disabled={resendCooldown > 0}
        className="text-left font-onest text-sm font-medium text-[#C01610] hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
      >
        {resendCooldown > 0
          ? `Resend code in ${formatTime(resendCooldown)}`
          : isExpired
            ? "Send new code"
            : "Resend code"}
      </button>
    </div>
  );
};

// Main Component
const OtEnquiryForm = ({
  onDataChange,
  defaultData,
  requireEmailVerification = true,
}: OtEnquiryFormProps) => {
  const [formData, setFormData] = useState<OtEnquiryFormData>({
    firstName: defaultData?.firstName || "",
    lastName: defaultData?.lastName || "",
    email: defaultData?.email || "",
    phone: defaultData?.phone || "",
    dateOfBirth: defaultData?.dateOfBirth || "",
    address: defaultData?.address || "",
    preferredDate: defaultData?.preferredDate || "",
    reasonForEnquiry: defaultData?.reasonForEnquiry || "",
    emailVerified: defaultData?.emailVerified || false,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof OtEnquiryFormData, string>>>({});

  // OTP state
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [verifiedEmail, setVerifiedEmail] = useState("");
  
  // OTP timer state (5 minutes = 300 seconds)
  const [otpTimeRemaining, setOtpTimeRemaining] = useState(300);
  const [otpExpired, setOtpExpired] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // OTP mutations
  const [sendOtp, { isLoading: isSendingOtp }] = useSenOTPMutation();
  const [verifyOtp, { isLoading: isVerifyingOtp }] = useVerifyOTPMutation();

  // OTP countdown timer effect
  useEffect(() => {
    if (!showOtpInput || otpExpired) return;

    const timer = setInterval(() => {
      setOtpTimeRemaining((prev) => {
        if (prev <= 1) {
          setOtpExpired(true);
          setOtpError("Verification code has expired. Please request a new one.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showOtpInput, otpExpired]);

  // Resend cooldown timer effect
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const updateField = <K extends keyof OtEnquiryFormData>(
    field: K,
    value: OtEnquiryFormData[K]
  ) => {
    const newData = { ...formData, [field]: value };
    // Reset email verification if email changes
    if (field === "email" && value !== verifiedEmail) {
      newData.emailVerified = false;
      setShowOtpInput(false);
      setOtpValue("");
      setOtpError(null);
      setOtpTimeRemaining(300);
      setOtpExpired(false);
      setResendCooldown(0);
    }
    setFormData(newData);
    onDataChange?.(newData);
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSendOtp = async () => {
    if (!formData.email || resendCooldown > 0) return;
    setOtpError(null);
    try {
      await sendOtp({ email: formData.email }).unwrap();
      setShowOtpInput(true);
      setOtpTimeRemaining(300); // Reset to 5 minutes
      setOtpExpired(false);
      setOtpValue(""); // Clear any previous OTP value
      setResendCooldown(120); // 120 second (2 minute) cooldown before next resend
    } catch (error) {
      console.error("Failed to send OTP:", error);
      setOtpError("Failed to send verification code. Please try again.");
    }
  };

  const handleVerifyOtp = async () => {
    if (!formData.email || otpValue.length !== 6 || otpExpired) return;
    setOtpError(null);
    try {
      await verifyOtp({ email: formData.email, otp: otpValue }).unwrap();
      setVerifiedEmail(formData.email);
      const newData = { ...formData, emailVerified: true };
      setFormData(newData);
      onDataChange?.(newData);
      setShowOtpInput(false);
      setOtpValue("");
      setOtpTimeRemaining(300);
      setOtpExpired(false);
      setResendCooldown(0);
    } catch (error) {
      console.error("Failed to verify OTP:", error);
      setOtpError("Invalid code. Please check and try again.");
    }
  };

  const isEmailVerified = formData.emailVerified && formData.email === verifiedEmail;

  return (
    <div className="flex flex-col gap-6 md:gap-10">
      {/* Header */}
      <div className="flex flex-col">
        <h2 className="p-2.5 font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
          Occupational Therapy Enquiry
        </h2>
        <p className="max-w-[700px] p-2.5 font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
          Please provide your information and our Occupational Therapy team will
          contact you shortly to discuss your needs and schedule an appointment.
        </p>
      </div>

      {/* Form Section */}
      <div className="flex flex-col gap-6 md:gap-[30px]">
        {/* Personal Information */}
        <div className="flex flex-col gap-2.5">
          <div className="p-2.5">
            <h3 className="font-onest text-xl font-semibold text-black md:text-2xl">
              Personal Information
            </h3>
          </div>

          <div className="flex flex-col">
            {/* First Name & Last Name */}
            <div className="flex flex-col sm:flex-row">
              <div className="flex flex-1 flex-col gap-2 p-2.5">
                <FormLabel label="First Name" required />
                <FormInput
                  placeholder="Enter first name"
                  value={formData.firstName}
                  onChange={(value) => updateField("firstName", value)}
                />
                {errors.firstName && (
                  <span className="font-onest text-sm text-red-500">{errors.firstName}</span>
                )}
              </div>
              <div className="flex flex-1 flex-col gap-2 p-2.5">
                <FormLabel label="Last Name" required />
                <FormInput
                  placeholder="Enter last name"
                  value={formData.lastName}
                  onChange={(value) => updateField("lastName", value)}
                />
                {errors.lastName && (
                  <span className="font-onest text-sm text-red-500">{errors.lastName}</span>
                )}
              </div>
            </div>

            {/* Date of Birth */}
            <div className="flex flex-col sm:flex-row">
              <div className="flex flex-1 flex-col gap-2 p-2.5">
                <FormLabel label="Date of Birth" />
                <FormInput
                  placeholder="Pick your date of birth"
                  value={formData.dateOfBirth}
                  onChange={(value) => updateField("dateOfBirth", value)}
                  type="date"
                />
              </div>
              <div className="flex flex-1 flex-col gap-2 p-2.5">
                {/* Empty for alignment */}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="flex flex-col gap-2.5">
          <div className="p-2.5">
            <h3 className="font-onest text-xl font-semibold text-black md:text-2xl">
              Contact Information
            </h3>
          </div>

          <div className="flex flex-col">
            {/* Email & Phone */}
            <div className="flex flex-col sm:flex-row">
              <div className="flex flex-1 flex-col gap-2 p-2.5">
                <FormLabel label="Email Address" required />
                <div className="flex gap-2">
                  <div className="flex-1">
                    <FormInput
                      placeholder="demo@email.com"
                      value={formData.email}
                      onChange={(value) => updateField("email", value)}
                      type="email"
                      icon={
                        isEmailVerified ? (
                          <CheckCircle className="size-5 text-green-500" />
                        ) : undefined
                      }
                    />
                  </div>
                  {requireEmailVerification && !isEmailVerified && formData.email && (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={isSendingOtp || !formData.email}
                      className="shrink-0 rounded-[10px] border border-[#C01610] px-4 py-2 font-onest text-sm font-medium text-[#C01610] transition-colors hover:bg-[#C01610]/5 disabled:opacity-50"
                    >
                      {isSendingOtp ? (
                        <Loader2 className="size-5 animate-spin" />
                      ) : (
                        "Verify"
                      )}
                    </button>
                  )}
                </div>
                {isEmailVerified && (
                  <span className="font-onest text-sm text-green-600">
                    Email verified
                  </span>
                )}
                {showOtpInput && !isEmailVerified && (
                  <OtpInput
                    value={otpValue}
                    onChange={setOtpValue}
                    onVerify={handleVerifyOtp}
                    onResend={handleSendOtp}
                    isVerifying={isVerifyingOtp}
                    error={otpError}
                    timeRemaining={otpTimeRemaining}
                    resendCooldown={resendCooldown}
                    isExpired={otpExpired}
                  />
                )}
                {errors.email && (
                  <span className="font-onest text-sm text-red-500">{errors.email}</span>
                )}
              </div>
              <div className="flex flex-1 flex-col gap-2 p-2.5">
                <FormLabel
                  label="Phone Number"
                  required
                  hint="(We will use this number to contact you)"
                />
                <FormInput
                  placeholder="+61 400 000 000"
                  value={formData.phone}
                  onChange={(value) => updateField("phone", value)}
                  type="tel"
                />
                {errors.phone && (
                  <span className="font-onest text-sm text-red-500">{errors.phone}</span>
                )}
              </div>
            </div>

            {/* Address */}
            <div className="flex flex-col">
              <div className="flex flex-1 flex-col gap-2 p-2.5">
                <FormLabel label="Address" hint="(Optional)" />
                <FormInput
                  placeholder="Enter your address"
                  value={formData.address}
                  onChange={(value) => updateField("address", value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Appointment Preferences */}
        <div className="flex flex-col gap-2.5">
          <div className="p-2.5">
            <h3 className="font-onest text-xl font-semibold text-black md:text-2xl">
              Appointment Preferences
            </h3>
          </div>

          <div className="flex flex-col">
            {/* Preferred Date */}
            <div className="flex flex-col sm:flex-row">
              <div className="flex flex-1 flex-col gap-2 p-2.5">
                <FormLabel label="Preferred Session Date" hint="(Optional)" />
                <FormInput
                  placeholder="Select a preferred date"
                  value={formData.preferredDate}
                  onChange={(value) => updateField("preferredDate", value)}
                  type="date"
                />
              </div>
              <div className="flex flex-1 flex-col gap-2 p-2.5">
                {/* Empty for alignment */}
              </div>
            </div>

            {/* Reason for Enquiry */}
            <div className="flex flex-col">
              <div className="flex flex-1 flex-col gap-2 p-2.5">
                <FormLabel label="Reason for Enquiry / Additional Notes" hint="(Optional)" />
                <textarea
                  value={formData.reasonForEnquiry}
                  onChange={(e) => updateField("reasonForEnquiry", e.target.value)}
                  placeholder="Please describe your needs or any specific concerns you would like to discuss with our Occupational Therapy team"
                  rows={5}
                  className="w-full resize-none overflow-hidden rounded-[10px] border border-[#C01610]/30 bg-transparent px-[17px] py-[11px] font-onest text-sm font-normal text-neutral-800 placeholder:text-neutral-500/50 focus:outline-none focus:ring-2 focus:ring-[#C01610]/30"
                />
              </div>
            </div>
          </div>
        </div>

        </div>
    </div>
  );
};

export default OtEnquiryForm;
